import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import { api } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { errorMessage } from '@/api/errors'
import type { AccessLevel, Section, User } from '@/types/api'

/**
 * Сесийна автентикация през Django: няма токен за пазене и няма какво да се
 * изгуби от localStorage. Състоянието е само огледало на `GET /auth/me/`.
 */
export const useAuth = defineStore('auth', () => {
  const user = ref<User | null>(null)
  /** Питали ли сме сървъра поне веднъж — пазачът на рутера чака това. */
  const resolved = ref(false)
  const pending = ref(false)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => user.value !== null)

  /** Ограничен потребител — само своите обекти и разделите на техника. */
  const restricted = computed(() => user.value?.restricted ?? false)
  const isAdmin = computed(() => user.value?.is_superuser ?? false)

  /** Само за менюто и бутоните; сървърът проверява всяка заявка сам. */
  function can(section: Section, need: AccessLevel = 'view'): boolean {
    const found = user.value?.sections[section]
    return found === 'edit' || (found === 'view' && need === 'view')
  }

  const displayName = computed(() => {
    if (!user.value) {
      return ''
    }
    const full = [user.value.first_name, user.value.last_name].filter(Boolean).join(' ').trim()
    return full || user.value.username
  })

  async function refresh(): Promise<User | null> {
    try {
      const { data } = await api.get<User>(endpoints.auth.me)
      user.value = data
    } catch {
      // 403 при липсваща сесия е нормалното състояние преди вход.
      user.value = null
    } finally {
      resolved.value = true
    }
    return user.value
  }

  /** Извиква се веднъж при първата навигация. */
  async function ensureResolved(): Promise<void> {
    if (!resolved.value) {
      await refresh()
    }
  }

  async function login(username: string, password: string): Promise<boolean> {
    pending.value = true
    error.value = null
    try {
      // Влизането минава без CSRF заглавка — DRF проверява CSRF само при вече
      // автентикирана сесия. Отговорът носи новите sessionid и csrftoken
      // бисквитки, с които всички следващи заявки са защитени.
      const { data } = await api.post<User>(endpoints.auth.login, { username, password })
      user.value = data
      resolved.value = true
      return true
    } catch (exception) {
      user.value = null
      error.value = errorMessage(exception, 'Входът не бе успешен.')
      return false
    } finally {
      pending.value = false
    }
  }

  async function logout(): Promise<void> {
    try {
      await api.post(endpoints.auth.logout)
    } finally {
      clear()
    }
  }

  /** Изчиства състоянието без заявка — при изтекла сесия. */
  function clear(): void {
    user.value = null
    resolved.value = true
    error.value = null
  }

  return {
    user,
    resolved,
    pending,
    error,
    isAuthenticated,
    restricted,
    isAdmin,
    can,
    displayName,
    refresh,
    ensureResolved,
    login,
    logout,
    clear,
  }
})
